import React, { useState, useRef, useCallback, useContext } from 'react';
import { Box, Typography } from '@mui/material';
import { alpha } from '@mui/material/styles';
import {
  LayersOutlined, DeleteOutlined,
  KeyboardArrowDownOutlined, KeyboardArrowRightOutlined,
  LockOutlined, WebOutlined, TableRowsOutlined,
} from '@mui/icons-material';
import { PRIMARY, CONTAINER_COLOR, TYPE_META } from '../constants';
import { CtxMenuCtx, DragCtx } from '../contexts';
import { useDragEndReset } from '../hooks';

// ─── LayerNode ────────────────────────────────────────────────────────────────
function LayerNode({ el, depth, idx, sectionId, containerId, selectedId, onSelect, onDeleteItem, onMoveFn, onRenameEl }) {
  const ctx       = useContext(DragCtx);
  const drag      = ctx.drag;
  const { open: openMenu } = useContext(CtxMenuCtx);
  const meta      = TYPE_META[el.type] || TYPE_META.text;
  const [open, setOpen] = useState(true);
  const [editing, setEditing] = useState(false);
  const hasKids   = el.type === 'container' && (el.children || []).length > 0;
  const isSelected = selectedId === el.id;

  const autoLabel = el.type === 'text'
    ? (el.content?.slice(0, 22) || 'Text')
    : el.type === 'button' ? (el.label || 'Button')
    : `Container (${(el.children || []).length})`;
  const label = el.name || autoLabel;
  const [draft, setDraft] = useState(label);

  const commitRename = () => {
    setEditing(false);
    const trimmed = draft.trim();
    if (!trimmed) { setDraft(label); return; }
    if (trimmed !== label) onRenameEl(el.id, trimmed);
  };

  return (
    <>
      <Box
        draggable={!editing}
        onDragStart={e => {
          if (editing) { e.preventDefault(); return; }
          e.stopPropagation();
          drag.current = { kind: 'layer', elId: el.id, fromContainerId: containerId, fromSectionId: sectionId, fromIdx: idx };
        }}
        onDragEnd={() => { drag.current = null; }}
        onClick={e => { e.stopPropagation(); if (!editing) onSelect(el.id); }}
        onContextMenu={e => { e.preventDefault(); e.stopPropagation(); onSelect(el.id); openMenu(el.id, e.clientX, e.clientY); }}
        sx={{
          display: 'flex', alignItems: 'center', gap: 0.5,
          pl: 1 + depth * 1.75, pr: 1, py: 0.65,
          borderRadius: 1.5, mx: 0.5, mb: 0.25,
          cursor: editing ? 'default' : 'grab', userSelect: 'none',
          background: isSelected ? alpha(meta.color, 0.1) : 'transparent',
          border: `1px solid ${isSelected ? alpha(meta.color, 0.25) : 'transparent'}`,
          transition: 'all 0.1s',
          '&:hover': { background: isSelected ? alpha(meta.color, 0.12) : alpha('#000', 0.04) },
          '&:hover .layer-del': { opacity: 1 },
          '&:active': { opacity: editing ? 1 : 0.6 },
        }}
      >
        {hasKids ? (
          <Box onClick={e => { e.stopPropagation(); setOpen(v => !v); }} sx={{ color: '#9ea8b3', display: 'flex', flexShrink: 0, cursor: 'pointer' }}>
            {open ? <KeyboardArrowDownOutlined sx={{ fontSize: 14 }} /> : <KeyboardArrowRightOutlined sx={{ fontSize: 14 }} />}
          </Box>
        ) : (
          <Box sx={{ width: 14, flexShrink: 0 }} />
        )}
        <Box sx={{ color: meta.color, display: 'flex', flexShrink: 0 }}>{meta.icon}</Box>

        {editing ? (
          <input autoFocus value={draft} onChange={e => setDraft(e.target.value)}
            onBlur={commitRename}
            onKeyDown={e => { e.stopPropagation(); if (e.key === 'Enter') e.target.blur(); if (e.key === 'Escape') { setDraft(label); setEditing(false); } }}
            onClick={e => e.stopPropagation()}
            style={{ flex: 1, minWidth: 0, fontSize: '0.75rem', fontWeight: 600, color: meta.color, background: 'transparent', border: 'none', borderBottom: `1px solid ${meta.color}`, outline: 'none', fontFamily: 'inherit', padding: '0 2px' }} />
        ) : (
          <Typography onDoubleClick={e => { e.stopPropagation(); setDraft(label); setEditing(true); }}
            sx={{ fontSize: '0.75rem', fontWeight: isSelected ? 700 : 500, color: isSelected ? meta.color : '#3c4555', flex: 1, minWidth: 0, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', cursor: 'text' }}>
            {label}
          </Typography>
        )}

        <Box className="layer-del" onClick={e => { e.stopPropagation(); onDeleteItem(el.id); }}
          sx={{ opacity: 0, transition: 'opacity 0.1s', color: '#d32f2f', display: 'flex', cursor: 'pointer', borderRadius: 0.75, p: 0.25, '&:hover': { background: alpha('#d32f2f', 0.1) }, flexShrink: 0 }}>
          <DeleteOutlined sx={{ fontSize: 12 }} />
        </Box>
      </Box>

      {el.type === 'container' && open && (
        <LayerList items={el.children || []} depth={depth + 1} sectionId={sectionId} containerId={el.id}
          selectedId={selectedId} onSelect={onSelect} onDeleteItem={onDeleteItem} onMoveFn={onMoveFn} onRenameEl={onRenameEl} />
      )}
    </>
  );
}

// ─── LayerList ────────────────────────────────────────────────────────────────
function LayerList({ items, depth, sectionId, containerId, selectedId, onSelect, onDeleteItem, onMoveFn, onRenameEl }) {
  const ctx = useContext(DragCtx);
  const drag = ctx.drag;
  const [overSlot, setOverSlot] = useState(null);
  const listRef = useRef();

  useDragEndReset(useCallback(() => { setOverSlot(null); }, []));

  const getSlot = (clientY) => {
    if (!listRef.current) return items.length;
    const rows = Array.from(listRef.current.querySelectorAll(':scope > [data-layer-row]'));
    for (let i = 0; i < rows.length; i++) {
      const r = rows[i].getBoundingClientRect();
      if (clientY < r.top + r.height / 2) return i;
    }
    return rows.length;
  };

  const indentPx = 8 + depth * 14;

  const DropLine = ({ show }) => show ? (
    <Box sx={{ height: 2, ml: `${indentPx}px`, background: CONTAINER_COLOR, borderRadius: 1, my: '2px', position: 'relative', zIndex: 10, boxShadow: `0 0 5px ${alpha(CONTAINER_COLOR, 0.4)}` }}>
      <Box sx={{ position: 'absolute', left: -4, top: -3, width: 8, height: 8, borderRadius: '50%', background: CONTAINER_COLOR }} />
    </Box>
  ) : null;

  const isEmptyContainer = items.length === 0 && containerId !== null;

  return (
    <Box ref={listRef} sx={{ position: 'relative', minHeight: isEmptyContainer ? 20 : undefined }}
      onDragOver={e => {
        const d = drag.current;
        if (!d || d.kind === 'palette') return;
        e.preventDefault(); e.stopPropagation();
        setOverSlot(getSlot(e.clientY));
      }}
      onDragLeave={e => { if (!e.currentTarget.contains(e.relatedTarget)) setOverSlot(null); }}
      onDrop={e => {
        e.preventDefault(); e.stopPropagation();
        const slot = getSlot(e.clientY);
        if (drag.current) onMoveFn(drag.current, sectionId, containerId, slot);
        drag.current = null;
        ctx.resetAll();
        setOverSlot(null);
      }}
    >
      {isEmptyContainer && (
        <Box sx={{ ml: `${indentPx + 18}px`, py: 0.5 }}>
          <Typography sx={{ fontSize: '0.68rem', color: alpha('#000', 0.25), fontStyle: 'italic' }}>Empty</Typography>
        </Box>
      )}
      <DropLine show={overSlot === 0} />
      {items.map((item, idx) => (
        <React.Fragment key={item.id}>
          <Box data-layer-row={idx}>
            <LayerNode el={item} depth={depth} idx={idx} sectionId={sectionId} containerId={containerId}
              selectedId={selectedId} onSelect={onSelect} onDeleteItem={onDeleteItem} onMoveFn={onMoveFn} onRenameEl={onRenameEl} />
          </Box>
          <DropLine show={overSlot === idx + 1} />
        </React.Fragment>
      ))}
    </Box>
  );
}

// ─── LayersPanel ──────────────────────────────────────────────────────────────
export function LayersPanel({ sections, selectedId, selectedSectionId, onSelect, onSelectSection, onDelete, onLayerMove, onSectionReorder, onSectionDuplicate, onSectionCopy, onSectionPaste, sectionClipboard, onRenameSection, onRenameEl, headerOverride, footerOverride }) {
  const ctx = useContext(DragCtx);
  const sectionDrag = ctx.sectionDrag;
  const { open: openMenu } = useContext(CtxMenuCtx);
  const [overSectionIdx, setOverSectionIdx] = useState(null);
  const [sectionOpen, setSectionOpen] = useState({});
  const [editingSectionId, setEditingSectionId] = useState(null);
  const [sectionDraft, setSectionDraft] = useState('');
  const listRef = useRef();

  const commitSectionRename = (section) => {
    setEditingSectionId(null);
    const trimmed = sectionDraft.trim();
    if (!trimmed) { setSectionDraft(section.label); return; }
    if (trimmed !== section.label) onRenameSection(section.id, trimmed);
  };

  const getSectionSlot = (clientY) => {
    if (!listRef.current) return sections.length;
    const rows = Array.from(listRef.current.querySelectorAll(':scope > [data-section-row]'));
    for (let i = 0; i < rows.length; i++) {
      const r = rows[i].getBoundingClientRect();
      if (clientY < r.top + r.height / 2) return i;
    }
    return rows.length;
  };

  // Whether a section is "pinned" (header or footer) — pinned rows are non-draggable and locked
  const isPinned = (section) => section.type === 'header' || section.type === 'footer';

  return (
    <Box onClick={e => e.stopPropagation()} sx={{ width: 220, flexShrink: 0, borderLeft: '1px solid #e8edf2', background: '#fafbfc', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
      <Box sx={{ px: 2, py: 1.75, borderBottom: '1px solid #e8edf2', display: 'flex', alignItems: 'center', gap: 1 }}>
        <LayersOutlined sx={{ fontSize: 15, color: '#9ea8b3' }} />
        <Typography sx={{ fontWeight: 700, fontSize: '0.75rem', color: 'text.secondary', textTransform: 'uppercase', letterSpacing: '0.08em' }}>Layers</Typography>
      </Box>
      <Box ref={listRef} sx={{ flex: 1, overflowY: 'auto', py: 1 }}
        onDragOver={e => { if (!sectionDrag.current) return; e.preventDefault(); setOverSectionIdx(getSectionSlot(e.clientY)); }}
        onDragLeave={e => { if (!e.currentTarget.contains(e.relatedTarget)) setOverSectionIdx(null); }}
        onDrop={e => {
          if (!sectionDrag.current) return;
          e.preventDefault();
          const fromIdx = sectionDrag.current.fromIdx;
          const slot = getSectionSlot(e.clientY);
          const toIdx = slot > fromIdx ? slot - 1 : slot;
          if (fromIdx !== toIdx) onSectionReorder(fromIdx, toIdx);
          sectionDrag.current = null;
          setOverSectionIdx(null);
        }}
      >
        {overSectionIdx === 0 && (
          <Box sx={{ height: 2, background: PRIMARY, borderRadius: 1, mx: 1, mb: 0.5, boxShadow: `0 0 5px ${alpha(PRIMARY, 0.4)}` }} />
        )}

        {sections.map((section, sIdx) => {
          const isSectionSelected = selectedSectionId === section.id;
          const pinned = isPinned(section);

          // Label colour: header=blue, footer=teal, others=normal
          const labelColor = section.type === 'header'
            ? '#1075BA'
            : section.type === 'footer'
              ? '#00897b'
              : isSectionSelected ? PRIMARY : '#9ea8b3';

          return (
            <Box key={section.id} data-section-row={sIdx} sx={{ mb: 0.25 }}>
              <Box
                draggable={editingSectionId !== section.id && !pinned}
                onDragStart={e => {
                  if (editingSectionId === section.id || pinned) { e.preventDefault(); return; }
                  e.stopPropagation();
                  sectionDrag.current = { fromIdx: sIdx };
                  const node = e.currentTarget;
                  const rect = node.getBoundingClientRect();
                  const clone = node.cloneNode(true);
                  Object.assign(clone.style, { position: 'fixed', left: '-9999px', top: '0', opacity: '0.85', background: '#fff', borderRadius: '6px', boxShadow: '0 4px 12px rgba(0,0,0,0.15)', width: rect.width + 'px' });
                  document.body.appendChild(clone);
                  e.dataTransfer.setDragImage(clone, e.clientX - rect.left, e.clientY - rect.top);
                  setTimeout(() => document.body.contains(clone) && document.body.removeChild(clone), 200);
                }}
                onDragEnd={() => { sectionDrag.current = null; setOverSectionIdx(null); }}
                onClick={e => { e.stopPropagation(); if (editingSectionId !== section.id) { onSelectSection(section.id); setSectionOpen(s => ({ ...s, [section.id]: s[section.id] !== false })); } }}
                onContextMenu={e => { e.preventDefault(); e.stopPropagation(); onSelectSection(section.id); openMenu(`section:${section.id}`, e.clientX, e.clientY); }}
                sx={{
                  display: 'flex', alignItems: 'center', gap: 0.75, px: 1.5, py: 0.75,
                  cursor: editingSectionId === section.id ? 'default' : pinned ? 'pointer' : 'grab',
                  userSelect: 'none',
                  borderRadius: 1.5, mx: 0.5,
                  background: isSectionSelected ? alpha(PRIMARY, 0.08) : 'transparent',
                  border: `1px solid ${isSectionSelected ? alpha(PRIMARY, 0.25) : 'transparent'}`,
                  transition: 'all 0.1s',
                  '&:hover': { background: isSectionSelected ? alpha(PRIMARY, 0.1) : alpha('#000', 0.04) },
                  '&:hover .layer-sec-del': { opacity: 1 },
                  '&:active': { cursor: editingSectionId === section.id ? 'default' : pinned ? 'pointer' : 'grabbing' },
                }}
              >
                {/* Expand/collapse chevron — only for regular sections */}
                {!pinned && (
                  <Box onClick={e => { e.stopPropagation(); setSectionOpen(s => ({ ...s, [section.id]: !sectionOpen[section.id] })); }} sx={{ display: 'flex', color: isSectionSelected ? PRIMARY : '#c0c8d4', cursor: 'pointer', flexShrink: 0 }}>
                    {sectionOpen[section.id] !== false
                      ? <KeyboardArrowDownOutlined sx={{ fontSize: 14 }} />
                      : <KeyboardArrowRightOutlined sx={{ fontSize: 14 }} />}
                  </Box>
                )}

                {/* Section label / rename input */}
                {editingSectionId === section.id ? (
                  <input autoFocus value={sectionDraft} onChange={e => setSectionDraft(e.target.value)}
                    onBlur={() => commitSectionRename(section)}
                    onKeyDown={e => { e.stopPropagation(); if (e.key === 'Enter') e.target.blur(); if (e.key === 'Escape') { setSectionDraft(section.label); setEditingSectionId(null); } }}
                    onClick={e => e.stopPropagation()}
                    style={{ flex: 1, minWidth: 0, fontSize: '0.68rem', fontWeight: 700, color: PRIMARY, background: 'transparent', border: 'none', borderBottom: `1px solid ${PRIMARY}`, outline: 'none', fontFamily: 'inherit', textTransform: 'uppercase', letterSpacing: '0.07em', padding: '0 2px' }} />
                ) : (
                  <Typography
                    onDoubleClick={e => {
                      e.stopPropagation();
                      if (pinned) return; // header and footer are not renameable
                      setSectionDraft(section.label);
                      setEditingSectionId(section.id);
                    }}
                    sx={{
                      fontSize: '0.68rem', fontWeight: 700,
                      color: labelColor,
                      textTransform: 'uppercase', letterSpacing: '0.07em',
                      flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                      cursor: pinned ? 'pointer' : 'text',
                      display: 'flex', alignItems: 'center', gap: 0.5,
                    }}
                  >
                    {section.type === 'header' && <WebOutlined sx={{ fontSize: 11 }} />}
                    {section.type === 'footer' && <TableRowsOutlined sx={{ fontSize: 11 }} />}
                    {section.label}
                    {/* Override badge */}
                    {section.type === 'header' && (
                      <Box component="span" sx={{ ml: 0.5, px: 0.6, py: 0.1, borderRadius: '3px', fontSize: '0.55rem', fontWeight: 800, lineHeight: 1, background: headerOverride ? alpha('#f59e0b', 0.15) : alpha('#1075BA', 0.12), color: headerOverride ? '#d97706' : '#1075BA', flexShrink: 0 }}>
                        {headerOverride ? '✶' : 'G'}
                      </Box>
                    )}
                    {section.type === 'footer' && (
                      <Box component="span" sx={{ ml: 0.5, px: 0.6, py: 0.1, borderRadius: '3px', fontSize: '0.55rem', fontWeight: 800, lineHeight: 1, background: footerOverride ? alpha('#f59e0b', 0.15) : alpha('#00897b', 0.12), color: footerOverride ? '#d97706' : '#00897b', flexShrink: 0 }}>
                        {footerOverride ? '✶' : 'G'}
                      </Box>
                    )}
                  </Typography>
                )}

                {/* Delete button (regular sections) or lock icon (pinned) */}
                {!pinned ? (
                  <Box className="layer-sec-del" onClick={e => { e.stopPropagation(); onDelete(section.id); }}
                    sx={{ opacity: 0, transition: 'opacity 0.1s', color: '#d32f2f', display: 'flex', cursor: 'pointer', borderRadius: 0.75, p: 0.25, '&:hover': { background: alpha('#d32f2f', 0.1) }, flexShrink: 0 }}>
                    <DeleteOutlined sx={{ fontSize: 12 }} />
                  </Box>
                ) : (
                  <LockOutlined sx={{ fontSize: 11, color: '#c0c8d4', flexShrink: 0 }} />
                )}
              </Box>

              {/* Element children — only shown for regular sections */}
              {!pinned && sectionOpen[section.id] !== false && (section.elements.length === 0 ? (
                <Box
                  onDragOver={e => { const d = ctx.drag.current; if (!d || d.kind === 'palette' || d.kind === 'section') return; e.preventDefault(); e.stopPropagation(); }}
                  onDrop={e => { e.preventDefault(); e.stopPropagation(); const d = ctx.drag.current; if (d && d.kind !== 'palette' && d.kind !== 'section') { onLayerMove(d, section.id, null, 0); ctx.drag.current = null; ctx.resetAll(); } }}
                  sx={{ pl: 3.5, py: 0.5 }}>
                  <Typography variant="caption" sx={{ color: alpha('#000', 0.25), fontStyle: 'italic', fontSize: '0.7rem', display: 'block' }}>Empty section</Typography>
                </Box>
              ) : (
                <Box sx={{ pl: 1.5 }}>
                  <LayerList items={section.elements} depth={0} sectionId={section.id} containerId={null}
                    selectedId={selectedId}
                    onSelect={id => { onSelect(id); onSelectSection(null); }}
                    onDeleteItem={(elId) => onDelete(section.id, elId)}
                    onMoveFn={onLayerMove}
                    onRenameEl={onRenameEl}
                  />
                </Box>
              ))}

              {overSectionIdx === sIdx + 1 && (
                <Box sx={{ height: 2, background: PRIMARY, borderRadius: 1, mx: 1, mt: 0.5, boxShadow: `0 0 5px ${alpha(PRIMARY, 0.4)}` }} />
              )}
            </Box>
          );
        })}
      </Box>
    </Box>
  );
}